import Wallet from '@/utils/wallet'
import { API_URL } from '@/utils/constants'
import { offerApi } from '@/utils/offer-api'
import {
	beginWalletFlow,
	classifyWalletError,
	errorDetail,
	finishWalletFlow,
	providerSlug,
	sanitizeCollectionSlug,
	telemetryAddress,
	telemetryPath,
	type WalletFlow
} from '@/utils/wallet/telemetry'
import type {
	WalletTelemetryErrorCategory,
	WalletTelemetryEvent,
	WalletTelemetryPayload,
	WalletTelemetryStage,
	WalletTelemetrySurface
} from '@/utils/wallet/telemetry-schema'

/**
 * Offer telemetry: one flow per attempt, the same funnel the purchase and
 * protected-listing flows use, so a failed offer, acceptance, decline,
 * refund or instant sell reaches the #errors channel with the phase it died
 * in, the code the API or wallet returned, the collection, the scope, the
 * first eight characters of the offer id, a shortened form of the buyer's
 * or seller's address (first five and last four characters) and the order
 * of magnitude of the offer amount. Nothing else leaves the page: no PSBTs,
 * keys, messages, whole addresses, exact amounts or whole ids.
 */
export type OfferTelemetryEvent = Extract<
	WalletTelemetryEvent,
	'offer_make' | 'offer_accept' | 'offer_decline' | 'offer_cancel' | 'offer_fill'
>
export type OfferTelemetrySurface = Extract<
	WalletTelemetrySurface,
	'offer_dialog' | 'offer_inbox' | 'instant_sell' | 'bids_page'
>
export type OfferTelemetryStage = Extract<
	WalletTelemetryStage,
	| 'build_offer'
	| 'sign_funding'
	| 'prepare_offer'
	| 'sign_offer'
	| 'activate_offer'
	| 'check_offer'
	| 'sign_accept'
	| 'submit_accept'
	| 'sign_refund'
	| 'submit_refund'
	| 'submit_decline'
	| 'unknown'
>

export interface OfferTelemetryContext {
	scope?: WalletTelemetryPayload['offer_scope']
	collectionSlug?: string | null
	priceSats?: number
	feeRate?: number
	/** The offer id once known; only its first 8 hex characters are sent. */
	offerId?: string | null
	/** Address on the side that failed: the buyer for bids/refunds, the seller for accept/fill. */
	role?: 'buyer' | 'seller'
}

export interface OfferFlow {
	/** Record the phase the attempt is in now, so a failure names it. */
	stage(next: OfferTelemetryStage): void
	/** Fill in context learned mid-flow (the offer id after build). */
	context(extra: Partial<OfferTelemetryContext>): void
	success(extra?: Partial<OfferTelemetryContext>): void
	failure(error: unknown, extra?: Partial<OfferTelemetryContext>): void
}

/** The wallet phase names the offer flows report through `onStep`. */
export const OFFER_STEP_STAGES: Record<string, OfferTelemetryStage> = {
	building: 'build_offer',
	sign_funding: 'sign_funding',
	preparing: 'prepare_offer',
	sign_offer: 'sign_offer',
	activating: 'activate_offer',
	checking: 'check_offer',
	signing: 'sign_refund',
	submitting: 'submit_refund'
}

/** Accept steps are human sentences; map them by what the sentence says. */
export const acceptStepStage = (step: string): OfferTelemetryStage => {
	const text = step.toLowerCase()
	if (text.includes('check')) return 'check_offer'
	if (text.includes('wallet') || text.includes('confirm')) return 'sign_accept'
	if (text.includes('submit')) return 'submit_accept'
	return 'unknown'
}

export const offerRef = (id: unknown): string | undefined => {
	if (typeof id !== 'string') return undefined
	const ref = id.replace(/-/g, '').slice(0, 8).toLowerCase()
	return /^[0-9a-f]{8}$/.test(ref) ? ref : undefined
}

/** Which offers API this flow talks to; `preview` is the isolated NAT/v2 API. */
export const offerApiTarget = (
	offerId?: string | null,
	scopeBody?: unknown
): WalletTelemetryPayload['offer_api'] => {
	try {
		const path = offerId ? `/market/offers/${offerId}/activate` : '/market/offers/build'
		return offerApi(path, scopeBody) === API_URL ? 'main' : 'preview'
	} catch {
		return undefined
	}
}

/**
 * The failure category for an offer error. Offer codes from the API say
 * what happened more precisely than the wallet classifier's message
 * matching, so the well-known ones map directly and the wallet classifier
 * covers what the wallet itself threw.
 */
export const offerErrorCategory = (error: any): WalletTelemetryErrorCategory => {
	try {
		const code = String(error?.code ?? '')
		if (code === 'insufficient_funds') return 'insufficient_funds'
		if (
			[
				'funding_not_signed',
				'seller_not_signed',
				'buyer_presignature_invalid',
				'seller_signature_invalid',
				'buyer_signature_invalid',
				'invalid_funding_psbt',
				'accept_template_mutated',
				'fill_template_mutated',
				'offer_intent_mismatch',
				'activation_unconfirmed',
				'funding_template_invalid',
				'funding_signature_invalid',
				'accept_template_invalid',
				'escrow_signature_invalid',
				'offer_template_oversigns',
				'offer_terms_invalid'
			].includes(code)
		)
			return 'invalid_response'
		if (
			[
				'cosigner_unavailable',
				'offers_paused',
				'rate_limited',
				'build_busy',
				'escrow_lookup_failed',
				'item_lookup_failed',
				'eligibility_lookup_failed',

				'funding_broadcast_failed',
				'broadcast_failed',
				'node_preflight_unavailable',
				'attempt_store_failed',
				'store_failed',
				'collection_lookup_failed',
				'funding_claim_unavailable',
				'asset_index_unavailable',
				'item_prevout_unavailable'
			].includes(code) ||
			/^http_5\d\d$/.test(code)
		)
			return 'network'
		return classifyWalletError(error)
	} catch {
		return 'unknown'
	}
}

const offerErrorCode = (error: any): string | undefined => {
	try {
		const raw = error?.code ?? error?.error?.code ?? error?.response?.status
		if (raw === undefined || raw === null) return undefined
		const code = String(raw)
			.replace(/[^A-Za-z0-9_.-]/g, '')
			.slice(0, 32)
		return code || undefined
	} catch {
		return undefined
	}
}

const providerNow = () => {
	try {
		return providerSlug(String(Wallet.type() || 'unknown'))
	} catch {
		return 'unknown' as const
	}
}

const addressFor = (role: OfferTelemetryContext['role']): string | undefined => {
	try {
		if (!Wallet.isSet()) return undefined
		const address = telemetryAddress(
			role === 'buyer' ? Wallet.balanceAddress() || Wallet.getTaprootAddress() : Wallet.getTaprootAddress()
		)
		return address ? shortTelemetryAddress(address) : undefined
	} catch {
		return undefined
	}
}

const numberOrUndefined = (value: unknown): number | undefined =>
	typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.round(value) : undefined

/**
 * First five and last four characters of an address: enough to recognise a
 * wallet across reports, not enough to look it up on-chain.
 */
export const shortTelemetryAddress = (address: string): string =>
	`${address.slice(0, 5)}…${address.slice(-4)}`

/**
 * The order of magnitude of an amount (3,274,431 sats reports as 1,000,000):
 * says how large the offer was without disclosing it.
 */
export const priceBucketSats = (sats: unknown): number | undefined => {
	const rounded = numberOrUndefined(sats)
	if (rounded === undefined) return undefined
	if (rounded < 1) return 0
	return 10 ** (String(rounded).length - 1)
}

export function beginOfferFlow(
	event: OfferTelemetryEvent,
	surface: OfferTelemetrySurface,
	initial: OfferTelemetryContext = {}
): OfferFlow {
	let context: OfferTelemetryContext = { ...initial }
	let stage: OfferTelemetryStage = 'unknown'
	let flow: WalletFlow | null = null
	let finished = false
	const baseDetails = () => ({
		collection_slug: sanitizeCollectionSlug(context.collectionSlug),
		offer_scope: context.scope,
		offer_api: offerApiTarget(context.offerId),
		offer_ref: offerRef(context.offerId)
	})
	try {
		flow = beginWalletFlow(event, providerNow(), surface, stage, baseDetails())
	} catch {
		flow = null
	}
	return {
		stage(next) {
			stage = next
		},
		context(extra) {
			context = { ...context, ...extra }
		},
		success(extra = {}) {
			if (finished || !flow) return
			finished = true
			context = { ...context, ...extra }
			try {
				finishWalletFlow(flow, stage, 'success', baseDetails())
			} catch {}
		},
		failure(error, extra = {}) {
			if (finished || !flow) return
			finished = true
			context = { ...context, ...extra }
			try {
				// The call that failed usually names the offer; keep it when the
				// caller did not know it yet (a build that failed mid-flight).
				const fromError = (error as { offerId?: unknown } | null)?.offerId
				if (!context.offerId && typeof fromError === 'string') context = { ...context, offerId: fromError }
				const category = offerErrorCategory(error)
				const role = context.role ?? (event === 'offer_accept' || event === 'offer_fill' ? 'seller' : 'buyer')
				const address = addressFor(role)
				finishWalletFlow(flow, stage, category === 'user_rejected' ? 'cancelled' : 'failure', {
					...baseDetails(),
					error_category: category,
					provider_error_code: offerErrorCode(error),
					...(category === 'user_rejected'
						? {}
						: {
								error_detail: errorDetail(error),
								path: telemetryPath(),
								price_sats: priceBucketSats(context.priceSats),
								fee_rate: numberOrUndefined(context.feeRate),
								...(address ? (role === 'buyer' ? { buyer_address: address } : { seller_address: address }) : {})
						  })
				})
			} catch {}
		}
	}
}
